import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from "react";

// Types
export interface User {
  id: string;
  token: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  name?: string;
  phone?: string;
  timezone?: string;
  [key: string]: any;
}

export interface UserState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

// Action types
type UserAction =
  | { type: "SET_USER"; payload: User }
  | { type: "CLEAR_USER" }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "UPDATE_USER"; payload: Partial<User> };

// Initialize user from localStorage synchronously
const initializeUserFromStorage = (): UserState => {
  const token = localStorage.getItem("token");
  const id = localStorage.getItem("id");
  const firstName = localStorage.getItem("firstName");
  const lastName = localStorage.getItem("lastName");
  const email = localStorage.getItem("email");
  const name = localStorage.getItem("name");
  const phone = localStorage.getItem("phone");
  const timezone = localStorage.getItem("timezone");

  if (token && id) {
    const user: User = {
      id,
      token,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      email: email || undefined,
      name: name || undefined,
      phone: phone || undefined,
      timezone: timezone || undefined,
    };
    return {
      user,
      isAuthenticated: true,
      loading: false,
      error: null,
    };
  }

  return {
    user: null,
    isAuthenticated: false,
    loading: false,
    error: null,
  };
};

// Initial state - initialize immediately from localStorage
const initialState: UserState = initializeUserFromStorage();

// Reducer
const userReducer = (state: UserState, action: UserAction): UserState => {
  switch (action.type) {
    case "SET_USER":
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        loading: false,
        error: null,
      };
    case "CLEAR_USER":
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };
    case "SET_LOADING":
      return {
        ...state,
        loading: action.payload,
      };
    case "SET_ERROR":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "UPDATE_USER":
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null,
      };
    default:
      return state;
  }
};

// Context type
interface UserContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (userData: User) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  getUserId: () => string | null;
  getToken: () => string | null;
  clearError: () => void;
  // Convenience getters
  firstName: string | undefined;
  lastName: string | undefined;
  email: string | undefined;
  name: string | undefined;
  phone: string | undefined;
  timezone: string | undefined;
}

// Create Context
const UserContext = createContext<UserContextType | undefined>(undefined);

// Provider Component
export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(userReducer, initialState);

  // User is now initialized synchronously in initialState
  // No need for useEffect delay

  // Login function
  const login = useCallback((userData: User) => {
    // Save to localStorage
    localStorage.setItem("token", userData.token);
    localStorage.setItem("id", userData.id);
    if (userData.firstName)
      localStorage.setItem("firstName", userData.firstName);
    if (userData.lastName) localStorage.setItem("lastName", userData.lastName);
    if (userData.email) localStorage.setItem("email", userData.email);
    if (userData.name) localStorage.setItem("name", userData.name);
    if (userData.phone) localStorage.setItem("phone", userData.phone);
    if (userData.timezone) localStorage.setItem("timezone", userData.timezone);
    dispatch({ type: "SET_USER", payload: userData });
  }, []);

  // Logout function
  const logout = useCallback(() => {
    // Clear localStorage
    localStorage.removeItem("token");
    localStorage.removeItem("id");
    localStorage.removeItem("firstName");
    localStorage.removeItem("lastName");
    localStorage.removeItem("email");
    localStorage.removeItem("name");
    localStorage.removeItem("phone");
    localStorage.removeItem("timezone");

    // Clear context
    dispatch({ type: "CLEAR_USER" });
  }, []);

  // Update user function
  const updateUser = useCallback((userData: Partial<User>) => {
    // Update localStorage
    if (userData.id) localStorage.setItem("id", userData.id);
    if (userData.token) localStorage.setItem("token", userData.token);
    if (userData.firstName)
      localStorage.setItem("firstName", userData.firstName);
    if (userData.lastName) localStorage.setItem("lastName", userData.lastName);
    if (userData.email) localStorage.setItem("email", userData.email);
    if (userData.name) localStorage.setItem("name", userData.name);
    if (userData.phone) localStorage.setItem("phone", userData.phone);
    if (userData.timezone !== undefined) {
      if (userData.timezone) localStorage.setItem("timezone", userData.timezone);
      else localStorage.removeItem("timezone");
    }
    // Update context
    dispatch({ type: "UPDATE_USER", payload: userData });
  }, []);

  // Helper functions
  const getUserId = useCallback(() => {
    return state.user?.id || null;
  }, [state.user]);

  const getToken = useCallback(() => {
    return state.user?.token || null;
  }, [state.user]);

  const clearError = useCallback(() => {
    dispatch({ type: "SET_ERROR", payload: null });
  }, []);

  const value: UserContextType = {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    error: state.error,
    login,
    logout,
    updateUser,
    getUserId,
    getToken,
    clearError,
    // Convenience getters
    firstName: state.user?.firstName,
    lastName: state.user?.lastName,
    email: state.user?.email,
    name: state.user?.name,
    phone: state.user?.phone,
    timezone: state.user?.timezone,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Custom hook to use the User Context
export const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};
